import {
    Box,
    List,
    Typography,
    IconButton,
    Tooltip
} from "@mui/material";
import { useEffect, useState } from "react";
import { getSandboxItems, runSandboxForId } from "../../../services/sandbox.service";
import SandboxDefinition from "../../../types/SandboxDefinition";
import SandboxListItem from "./SandboxListItem";
import { SandboxResult } from "../../../types/SandboxResult";
import { useNotifyMessage } from "../../../hooks/useNotifyMessage";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

interface SandboxItemListProps {
    onSelected?: (item: SandboxDefinition) => void;
    onResultRetrieved?: (item: SandboxDefinition, result: SandboxResult) => void;
    onItemDoubleClick?: (item: SandboxDefinition) => void;

    onAddClick?: () => void;
    onSaveClick?: () => void;
    onItemDelete?: (item: SandboxDefinition) => void; 

    /** ID of the currently selected item (controlled by parent) */
    selectedItemId?: string;

    /** When this value changes, the list will refetch items */
    reloadKey?: number;
}

type ItemState = {
    isLoading: boolean;
    result: SandboxResult | null;
};

export default function SandboxItemList(props: SandboxItemListProps) {
    const { notifyMessage } = useNotifyMessage();

    const [items, setItems] = useState<SandboxDefinition[]>([]);
    const [itemStates, setItemStates] = useState<Record<string, ItemState>>({});

    const initItemStates = (sandboxItems: SandboxDefinition[]) => {
        setItemStates(prev => {
            const copy = { ...prev };
            sandboxItems.forEach(i => {
                if (!copy[i.id]) {
                    copy[i.id] = { isLoading: false, result: null };
                }
            });
            return copy;
        });
    };

    const loadItems = () => {
        getSandboxItems().then(x => {
            if (x && x.data && x.data.results) {
                setItems(x.data.results);
                initItemStates(x.data.results);
            }
        });
    };

    useEffect(() => {
        loadItems();
        // reload whenever reloadKey changes
    }, [props.reloadKey]);

    const updateItemState = (id: string, patch: Partial<ItemState>) => {
        setItemStates(prev => ({
            ...prev,
            [id]: {
                ...(prev[id] ?? { isLoading: false, result: null }),
                ...patch
            }
        }));
    };

    const handleResultRetrieved = (item: SandboxDefinition, result: SandboxResult) => {
        updateItemState(item.id, { result });
        props.onResultRetrieved && props.onResultRetrieved(item, result);
    };

    const runItem = async (item: SandboxDefinition) => {
        const id = item.id;

        updateItemState(id, { isLoading: true });

        try {
            const response = await runSandboxForId(id);

            if (response.status === 200 && response.data) {
                debugger;
                const result = response.data as SandboxResult;
                handleResultRetrieved(item, result);
            } else {
                notifyMessage(response.data?.toString() ?? "Unknown error", "error");
            }
        } catch (err: any) {
            notifyMessage(err?.message ?? "Error running sandbox", "error");
        } finally {
            updateItemState(id, { isLoading: false });
        }
    };

    const handleRunAll = async () => {
        for (const item of items) {
            runItem(item);
        }
    };

    const itemClicked = (item: SandboxDefinition) => {
        props.onSelected && props.onSelected(item);
    };

    const itemDoubleClicked = (item: SandboxDefinition) => {
        props.onItemDoubleClick && props.onItemDoubleClick(item);
    };

    const itemDelete = (item: SandboxDefinition) => {
        props.onItemDelete && props.onItemDelete(item);
    };

    const anyLoading = Object.values(itemStates).some(s => s.isLoading);

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                p: 1
            }}
        >
            {/* Toolbar */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1
                }}
            >
                <Typography variant="subtitle1" fontWeight={600}>
                    Sandboxes
                </Typography>

                <Box>
                    {props.onAddClick && (
                        <Tooltip title="Add sandbox">
                            <IconButton
                                size="small"
                                onClick={props.onAddClick}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {props.onSaveClick && (
                        <Tooltip title="Save sandbox">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={props.onSaveClick}
                                    disabled={!props.selectedItemId}
                                >
                                    <SaveIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    <Tooltip title="Run all tests">
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleRunAll}
                                disabled={items.length === 0 || anyLoading}
                            >
                                <PlayArrowIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Scrollable list */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    mt: 0.5
                }}
            >
                <List
                    sx={{
                        width: "100%",
                        py: 0
                    }}
                    dense
                >
                    {items.map((item) => {
                        const state = itemStates[item.id] ?? {
                            isLoading: false,
                            result: null
                        };

                        return (
                            <SandboxListItem
                                key={item.id}
                                item={item}
                                isSelected={props.selectedItemId === item.id}
                                onSelect={() => itemClicked(item)}
                                onRun={() => runItem(item)}
                                isLoading={state.isLoading}
                                result={state.result}
                                onItemDoubleClicked={() => itemDoubleClicked(item)}
                                onItemDelete={() => itemDelete(item)}
                            />
                        );
                    })}
                </List>
            </Box>
        </Box>
    );
}
